import AppSidebar from "./AppSidebar";
import GlobalSearch from "./GlobalSearch";
import KeyboardShortcuts from "./KeyboardShortcuts";
import { useAuth, api } from "../App";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { sounds, showToast, celebrate, getGreeting, getWellnessTip } from "../hooks/useDMEngine";
import IdleScreensaver from "./IdleScreensaver";
import { DailyPriorityBanner, DailyPriorityModal, ReflectionModal } from "./DailyPriorities";
import EODUpdateOverlay from "./EODUpdateOverlay";
import PWAInstallPrompt from "./PWAInstallPrompt";
import MobileProfileSheet from "./MobileProfileSheet";
import useIsMobile from "../hooks/useIsMobile";

export default function AppShell({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [time, setTime] = useState("");
  const [idle, setIdle] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [streak, setStreak] = useState(0);
  const [recentActivity, setRecentActivity] = useState(null);
  const prevNotifsRef = useRef([]);
  const notifRef = useRef(null);
  const [dailyPriorities, setDailyPriorities] = useState(null);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [prioritiesBlocked, setPrioritiesBlocked] = useState(false);
  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const [showEODOverlay, setShowEODOverlay] = useState(false);
  const eodCheckedRef = useRef(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const isMobile = useIsMobile();
  const [missingReviewers, setMissingReviewers] = useState(null);

  // Listen for profile open events from child components
  useEffect(() => {
    const handler = () => setProfileOpen(true);
    window.addEventListener('openProfile', handler);
    return () => window.removeEventListener('openProfile', handler);
  }, []);

  // Check for tasks/briefs missing reviewers — show once per day
  useEffect(() => {
    if (!user) return;
    const today = new Date().toISOString().slice(0, 10);
    const lastCheck = localStorage.getItem('reviewer_check_date');
    if (lastCheck === today) return;
    api.get('/me/missing-reviewers').then(r => {
      if (r.data?.total > 0) setMissingReviewers(r.data);
      localStorage.setItem('reviewer_check_date', today);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }));
    tick(); const i = setInterval(tick, 1000); return () => clearInterval(i);
  }, []);

  // EOD task status-update overlay removed (was prompting users to update task
  // statuses) — intentionally no longer shown.

  // Notification polling + celebration detection for Won briefs
  useEffect(() => {
    if (!user) return;
    // Consolidated heartbeat — single 60s tick fetches notifications, recent
    // activity, AND touches session presence in one request. Replaces three
    // previously-independent setIntervals (notifs/30s + POST heartbeat/60s +
    // activity/60s) so two concurrent users produce 2 req/min instead of 12.
    const tick = async () => {
      try {
        const r = await api.get("/me/heartbeat");
        const data = r.data || {};
        const fresh = data.notifications || [];
        // Celebrate newly-won briefs we hadn't seen before
        const prevIds = new Set(prevNotifsRef.current.map(n => n.notification_id));
        const newWins = fresh.filter(n => n.type === "brief_won" && !n.read_at && !prevIds.has(n.notification_id));
        if (newWins.length > 0 && prevNotifsRef.current.length > 0) {
          celebrate(newWins[0].message || "🎉 Brief Won!");
        }
        prevNotifsRef.current = fresh;
        setNotifs(fresh);
        if (data.recent_activity) setRecentActivity(data.recent_activity);
      } catch (err) {
        console.warn("[Heartbeat] fetch failed:", err?.response?.status, err?.message);
      }
    };
    tick();
    const i = setInterval(tick, 60000);
    return () => clearInterval(i);
  }, [user]);

  // Hide Emergent badge on mobile (inline !important can't be overridden by CSS)
  useEffect(() => {
    if (window.innerWidth < 768) {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.style.setProperty('display', 'none', 'important');
    }
  }, []);

  // Session presence — folded into the consolidated /me/heartbeat poll above.

  // "3 priorities for the day" removed — no fetch, no banner, no blocking gate.
  const loadDailyPriorities = () => {};

  // Close notification dropdown on outside click
  useEffect(() => {
    if (!showNotifs) return;
    const close = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showNotifs]);

  // Keyboard shortcuts (? key, N key, G+key navigation)
  const gPendingRef = useRef(false);
  const gTimerRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
      if (e.key === "?" && !e.metaKey && !e.ctrlKey) { e.preventDefault(); setShowShortcuts(s => !s); return; }
      if (e.key === "Escape") { setShowShortcuts(false); return; }
      if (searchOpen || showShortcuts) return;
      if (e.key === "n" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        const path = window.location.pathname;
        if (path.startsWith("/tasks")) navigate("/tasks/new");
        else if (path.startsWith("/bizdev/leads")) navigate("/bizdev/leads?new=1");
        else if (path.startsWith("/bizdev/accounts")) navigate("/bizdev/leads?new=1");
        else if (path.startsWith("/bizdev/briefs") || path.startsWith("/bizdev")) navigate("/bizdev/briefs?new=1");
        else navigate("/bizdev/briefs?new=1");
        return;
      }
      // G+key navigation
      if (e.key === "g" && !e.metaKey && !e.ctrlKey) { gPendingRef.current = true; clearTimeout(gTimerRef.current); gTimerRef.current = setTimeout(() => { gPendingRef.current = false; }, 800); return; }
      if (gPendingRef.current) {
        gPendingRef.current = false; clearTimeout(gTimerRef.current);
        const routes = { d: "/me", b: "/bizdev/briefs", l: "/bizdev/leads", t: "/tasks", a: "/bizdev/accounts" };
        if (routes[e.key]) { e.preventDefault(); navigate(routes[e.key]); }
      }
    };
    window.addEventListener("keydown", handler);
    return () => { window.removeEventListener("keydown", handler); clearTimeout(gTimerRef.current); };
  }, [searchOpen, showShortcuts, navigate]);

  // Login streak tracker
  useEffect(() => {
    const key = "dm_streak";
    const lastKey = "dm_last_login";
    const today = new Date().toDateString();
    const last = localStorage.getItem(lastKey);
    if (last === today) {
      setStreak(parseInt(localStorage.getItem(key) || "1", 10));
    } else {
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      const s = last === yesterday ? (parseInt(localStorage.getItem(key) || "0", 10) + 1) : 1;
      localStorage.setItem(key, s); localStorage.setItem(lastKey, today);
      setStreak(s);
      if (s > 1) setTimeout(() => showToast(`${s}-day streak! Keep it up.`), 2000);
    }
  }, []);

  // Activity pulse — folded into the consolidated /me/heartbeat poll above.

  // Idle detection (15 minutes)
  useEffect(() => {
    let timer;
    const IDLE_MS = 15 * 60 * 1000;
    const reset = () => { clearTimeout(timer); setIdle(false); timer = setTimeout(() => setIdle(true), IDLE_MS); };
    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); events.forEach(e => window.removeEventListener(e, reset)); };
  }, []);

  const unread = notifs.filter(n => !n.read_at).length;
  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); setNotifs(ns => ns.map(n => n.notification_id === id ? { ...n, read_at: new Date().toISOString() } : n)); };
  const markAllRead = async () => {
    try { await api.post("/notifications/mark-all-read"); setNotifs(ns => ns.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))); }
    catch {}
  };

  // Navigate based on entity type
  const navToNotif = (n) => {
    markRead(n.notification_id);
    setShowNotifs(false);
    if (n.entity_type === "task") navigate(`/tasks/${n.entity_id}`);
    else if (n.entity_type === "brief") navigate(`/bizdev/briefs/${n.entity_id}`);
    else if (n.entity_type === "lead") navigate(`/bizdev/leads/${n.entity_id}`);
    else if (n.entity_type === "project") navigate(`/projects/${n.entity_id}`);
    else if (n.entity_type === "asset") navigate(`/production/assets/${n.entity_id}`);
    else if (n.entity_type === "bug") navigate(`/admin/bugs`);
    else if (n.entity_id) navigate(`/bizdev/briefs/${n.entity_id}`);
  };

  // Group notifications
  const now = new Date();
  const todayStr = now.toDateString();
  const yesterdayStr = new Date(now - 86400000).toDateString();
  const grouped = { today: [], yesterday: [], earlier: [] };
  notifs.slice(0, 20).forEach(n => {
    const d = new Date(n.created_at).toDateString();
    if (d === todayStr) grouped.today.push(n);
    else if (d === yesterdayStr) grouped.yesterday.push(n);
    else grouped.earlier.push(n);
  });

  // Notification type icons
  const notifIcon = (type) => {
    if (type === "brief_won") return "🎉";
    if (type === "task_assigned") return "📋";
    if (type === "brief_assigned") return "📄";
    if (type === "brief_status_change") return "📊";
    if (type === "task_status_change") return "🔄";
    if (type === "task_completed") return "✅";
    if (type === "review_request") return "👀";
    if (type === "mention") return "💬";
    if (type === "reply") return "↩️";
    if (type === "bug_resolved") return "🐛";
    return "🔔";
  };

  const timeAgo = (dateStr) => {
    const diff = (now - new Date(dateStr)) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (!user) return children;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'var(--sidebar-width) 1fr', minHeight: '100vh', background: 'var(--bg)', position: 'relative', zIndex: 2 }} className="dm-layout-grid">
      {!isMobile && <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />}

      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', gridColumn: '2 / -1', background: isMobile ? '#FAFAFA' : undefined }} className="dm-main-col">
        {/* Topbar — IG white on mobile, dark on desktop */}
        {isMobile ? (
          <header style={{ position: 'sticky', top: 0, zIndex: 50, height: 44, background: '#FFFFFF', borderBottom: '1px solid #EFEFEF', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px' }}>
            {/* Left: + Create */}
            <div style={{ position: 'absolute', left: 16, cursor: 'pointer' }} onClick={() => setCreateOpen(true)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="5"/><path d="M12 8v8M8 12h8"/></svg>
            </div>
            {/* Center: DENTSU AMP */}
            <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: '.06em', color: '#262626' }}>DENTSU AMP</span>
            {/* Right: Notification bell */}
            <div style={{ position: 'absolute', right: 16, cursor: 'pointer' }} onClick={() => setShowNotifs(!showNotifs)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="1.5"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
              {notifs.length > 0 && <div style={{ position: 'absolute', top: -2, right: -2, width: 7, height: 7, borderRadius: '50%', background: '#EF4444', border: '1.5px solid #FFF' }} />}
            </div>
          </header>
        ) : (
        <header style={{ position: 'sticky', top: 0, zIndex: 50, height: 'var(--topbar-height)', background: 'rgba(8,8,15,.92)', backdropFilter: 'blur(14px)', borderBottom: '1px solid var(--bdr)', display: 'flex', alignItems: 'center', padding: '0 var(--pad)', gap: 12, animation: 'dmSlideInDown .4s cubic-bezier(.34,1.56,.64,1) .1s both' }}>
          {/* Hamburger (mobile) */}
          <div onClick={() => setSidebarOpen(true)} style={{ display: 'none', cursor: 'pointer', padding: 6, color: 'var(--t2)' }} className="dm-ham-btn">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 4h14M2 9h14M2 14h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </div>

          {/* Search */}
          <div className="dm-search-bar" onClick={() => setSearchOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--s2)', border: '1px solid var(--bdr)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', maxWidth: 280, flex: 1, transition: 'border-color .15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--bdrh)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--bdr)'}
            data-testid="global-search-btn">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="5" cy="5" r="3.5" stroke="var(--t3)" strokeWidth="1.2"/><path d="M8 8l2.5 2.5" stroke="var(--t3)" strokeWidth="1.2" strokeLinecap="round"/></svg>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--t3)', letterSpacing: '.02em' }}>Search everything...</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--t4)', background: 'var(--s3)', border: '1px solid var(--bdr)', borderRadius: 3, padding: '1px 5px', marginLeft: 'auto' }} className="dm-search-kbd">⌘K</span>
          </div>

          <div className="dm-topbar-right" style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            {/* Activity Pulse */}
            {recentActivity && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'rgba(6,182,212,.06)', border: '1px solid rgba(6,182,212,.1)', borderRadius: 4, maxWidth: 220, overflow: 'hidden' }} className="dm-activity-pulse" title={recentActivity.summary}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#06B6D4', animation: 'dmPip 1.5s ease-in-out infinite', flexShrink: 0 }} />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--t3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{recentActivity.actor_name?.split(' ')[0]}: {recentActivity.summary?.slice(0, 35)}{recentActivity.summary?.length > 35 ? '…' : ''}</span>
              </div>
            )}

            {/* Streak badge */}
            {streak > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.15)', borderRadius: 4, cursor: 'default' }} title={`${streak}-day login streak`} className="dm-streak-badge">
                <span style={{ fontSize: 11 }}>🔥</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#F59E0B', fontWeight: 600 }}>{streak}</span>
              </div>
            )}

            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--t3)', letterSpacing: '.1em', whiteSpace: 'nowrap', minWidth: 64 }} className="dm-clock">{time}</span>

            {/* Notification bell */}
            <div style={{ position: 'relative' }} ref={notifRef}>
              <div onClick={() => setShowNotifs(!showNotifs)} style={{ padding: 6, cursor: 'pointer', color: 'var(--t2)', transition: 'color .15s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--t1)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--t2)'}
                data-testid="notif-bell">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 12.5a2 2 0 004 0M3 6.5a5 5 0 0110 0c0 2.5 1 4 1 4H2s1-1.5 1-4z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                {unread > 0 && <div style={{ position: 'absolute', top: 2, right: 2, minWidth: 14, height: 14, borderRadius: 7, background: 'var(--r)', border: '1.5px solid var(--s1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontFamily: 'var(--font-mono)', color: '#fff', fontWeight: 700, padding: '0 3px' }}>{unread > 9 ? "9+" : unread}</div>}
              </div>
              {showNotifs && (
                <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, width: 'min(340px, calc(100vw - 32px))', background: 'var(--s1)', border: '1px solid var(--bdr)', borderRadius: 'var(--radius)', maxHeight: 420, overflowY: 'auto', zIndex: 60, boxShadow: '0 12px 40px rgba(0,0,0,.5)' }}>
                  {/* Header */}
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--bdr)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--t2)', letterSpacing: '.1em' }}>NOTIFICATIONS {unread > 0 && <span style={{ color: 'var(--r)' }}>({unread})</span>}</span>
                    {unread > 0 && <button onClick={markAllRead} style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--c)', background: 'none', border: 'none', cursor: 'pointer' }}>Mark all read</button>}
                  </div>
                  {notifs.length === 0 ? (
                    <div style={{ padding: 30, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--t3)' }}>No notifications yet</div>
                  ) : (
                    <>
                      {grouped.today.length > 0 && (
                        <>
                          <div style={{ padding: '6px 14px', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--t3)', letterSpacing: '.15em', textTransform: 'uppercase' }}>Today</div>
                          {grouped.today.map(n => (
                            <div key={n.notification_id} onClick={() => navToNotif(n)}
                              style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,.03)', cursor: 'pointer', background: !n.read_at ? 'rgba(227,25,55,.03)' : 'transparent', transition: 'background .15s', display: 'flex', gap: 10, alignItems: 'flex-start' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.03)'}
                              onMouseLeave={e => e.currentTarget.style.background = !n.read_at ? 'rgba(227,25,55,.03)' : 'transparent'}>
                              <span style={{ fontSize: 14, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>{notifIcon(n.type)}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: !n.read_at ? 'var(--t1)' : 'var(--t2)', lineHeight: 1.4 }}>{n.message}</div>
                                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--t3)', marginTop: 3 }}>{timeAgo(n.created_at)}</div>
                              </div>
                              {!n.read_at && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--r)', flexShrink: 0, marginTop: 6 }} />}
                            </div>
                          ))}
                        </>
                      )}
                      {grouped.yesterday.length > 0 && (
                        <>
                          <div style={{ padding: '6px 14px', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--t3)', letterSpacing: '.15em', textTransform: 'uppercase' }}>Yesterday</div>
                          {grouped.yesterday.map(n => (
                            <div key={n.notification_id} onClick={() => navToNotif(n)}
                              style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,.03)', cursor: 'pointer', background: !n.read_at ? 'rgba(227,25,55,.03)' : 'transparent', transition: 'background .15s', display: 'flex', gap: 10, alignItems: 'flex-start' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.03)'}
                              onMouseLeave={e => e.currentTarget.style.background = !n.read_at ? 'rgba(227,25,55,.03)' : 'transparent'}>
                              <span style={{ fontSize: 14, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>{notifIcon(n.type)}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: !n.read_at ? 'var(--t1)' : 'var(--t2)', lineHeight: 1.4 }}>{n.message}</div>
                                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--t3)', marginTop: 3 }}>{timeAgo(n.created_at)}</div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                      {grouped.earlier.length > 0 && (
                        <>
                          <div style={{ padding: '6px 14px', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--t3)', letterSpacing: '.15em', textTransform: 'uppercase' }}>Earlier</div>
                          {grouped.earlier.map(n => (
                            <div key={n.notification_id} onClick={() => navToNotif(n)}
                              style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,.03)', cursor: 'pointer', background: 'transparent', transition: 'background .15s', display: 'flex', gap: 10, alignItems: 'flex-start' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.03)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <span style={{ fontSize: 14, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>{notifIcon(n.type)}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--t2)', lineHeight: 1.4 }}>{n.message}</div>
                                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--t3)', marginTop: 3 }}>{timeAgo(n.created_at)}</div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Shortcuts hint */}
            <div className="dm-shortcuts-hint" onClick={() => setShowShortcuts(true)} style={{ padding: '4px 8px', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--t4)', border: '1px solid var(--bdr)', borderRadius: 4, transition: 'all .15s' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--t2)'; e.currentTarget.style.borderColor = 'var(--bdrh)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--t4)'; e.currentTarget.style.borderColor = 'var(--bdr)'; }}
              title="Keyboard shortcuts">?</div>
          </div>
          <div className="dm-mobile-only" style={{ display: 'none' }} onClick={() => setProfileOpen(true)}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--r)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', cursor: 'pointer', border: '2px solid var(--bdr)' }}>
              {(user?.name || user?.full_name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
            </div>
          </div>
        </header>
        )}

        {/* Content */}
        <main style={{ flex: 1, padding: isMobile ? '0 0 60px' : 'var(--pad)', paddingBottom: isMobile ? 60 : 80, position: 'relative', zIndex: 2, background: isMobile ? '#FAFAFA' : undefined }} className="dm-main-content">
          {/* Missing reviewer prompt */}
          {missingReviewers && missingReviewers.total > 0 && (() => {
            const names = [...missingReviewers.tasks.map(t => t.title), ...missingReviewers.briefs.map(b => b.title)];
            const preview = names.slice(0, 2).join(", ");
            const more = names.length > 2 ? ` +${names.length - 2} more` : "";
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'rgba(245,158,11,.06)', border: '1px solid rgba(245,158,11,.15)', borderRadius: 10, marginBottom: 10, fontSize: 12 }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: 'var(--t1)' }}>
                    {missingReviewers.total} {missingReviewers.total === 1 ? 'item' : 'items'} missing a reviewer
                  </div>
                  <div style={{ color: 'var(--t3)', fontSize: 11, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {preview}{more}
                  </div>
                </div>
                <button onClick={() => navigate('/fix-reviewers')}
                  style={{ padding: '5px 12px', borderRadius: 6, background: '#F59E0B', color: '#fff', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Fix now
                </button>
                <button onClick={() => setMissingReviewers(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--t4)', cursor: 'pointer', fontSize: 14 }}>✕</button>
              </div>
            );
          })()}
          {/* Daily 3 — slim ticker bar.
              Hidden on /me because MyDayHeader already renders the same
              priorities at the top of the list there (avoids double-display
              that users flagged as confusing). Kept on every other page so
              priorities stay visible while you're working in Production,
              Briefs, etc. */}
          {dailyPriorities?.today && location.pathname !== "/me" && (() => {
            const p = dailyPriorities.today.priorities || [];
            const done = p.filter(x => x.completed).length;
            return (
              <div className="dm-ticker" style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: "linear-gradient(135deg, rgba(227,25,55,.04), rgba(168,85,247,.02))", border: "1px solid rgba(227,25,55,.1)", marginBottom: 10, borderRadius: 10, fontSize: 12 }}>
                <span style={{ fontSize: 11, color: "var(--r)", fontFamily: "var(--font-mono)", fontWeight: 700, flexShrink: 0 }}>🎯 {done}/{p.length}</span>
                <div style={{ display: "flex", gap: 16, flex: 1, overflow: "hidden" }}>
                  {p.map((item, i) => (
                    <button key={i} onClick={async () => { try { await api.put(`/me/daily-priorities/${i}/complete`); loadDailyPriorities(); } catch {} }}
                      style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: item.completed ? "var(--t4)" : "var(--t2)", fontSize: 12, fontFamily: "var(--font-sans)", whiteSpace: "nowrap", textDecoration: item.completed ? "line-through" : "none", opacity: item.completed ? 0.5 : 1, transition: "all .15s" }}>
                      <span style={{ width: 14, height: 14, borderRadius: 4, border: item.completed ? "1.5px solid #22C55E" : "1.5px solid var(--bdr)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, background: item.completed ? "rgba(34,197,94,.15)" : "transparent", color: "#22C55E", flexShrink: 0 }}>{item.completed ? "✓" : ""}</span>
                      {item.text}
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowReflectionModal(true)} style={{ background: "none", border: "none", color: "var(--t4)", fontSize: 10, cursor: "pointer", fontFamily: "var(--font-mono)", whiteSpace: "nowrap", opacity: 0.6 }}>blocked?</button>
              </div>
            );
          })()}
          {/* Set priorities prompt — slim */}
          {dailyPriorities && !dailyPriorities.today && location.pathname !== "/login" && location.pathname !== "/signup" && (
            <div onClick={() => setShowPriorityModal(true)}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", background: "rgba(227,25,55,.02)", borderBottom: "1px solid rgba(227,25,55,.08)", marginBottom: 8, borderRadius: 8, cursor: "pointer", fontSize: 12, transition: "background .15s" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(227,25,55,.05)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(227,25,55,.02)"}>
              <span>🎯</span>
              <span style={{ color: "var(--t2)", fontSize: 12 }}>Set your 3 priorities for today</span>
              <span style={{ color: "var(--r)", fontFamily: "var(--font-mono)", fontSize: 10, marginLeft: "auto" }}>&rarr;</span>
            </div>
          )}
          {prioritiesBlocked ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh", textAlign: "center" }}>
              <div>
                <div style={{ fontSize: 40, marginBottom: 16 }}>🎯</div>
                <p style={{ fontSize: 18, fontWeight: 700, color: "var(--t1)", marginBottom: 8, fontFamily: "var(--font-orb)" }}>Set your priorities first</p>
                <p style={{ fontSize: 13, color: "var(--t3)", marginBottom: 20 }}>You need to set today's 3 priorities before using the platform.</p>
                <button onClick={() => setShowPriorityModal(true)} style={{ padding: "10px 24px", background: "var(--r)", color: "#fff", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>
                  Set Priorities
                </button>
              </div>
            </div>
          ) : children}
        </main>
      </div>

      {/* ═══ BOTTOM NAV — IG style, mobile only ═══ */}
      {isMobile && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
          background: '#FFFFFF', borderTop: '1px solid #EFEFEF',
          height: 50, display: 'flex', alignItems: 'center',
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
          maxWidth: '100vw',
        }}>
          {[
            { path: "/me", icon: (a) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={a ? '#262626' : '#8E8E8E'} strokeWidth={a ? 2 : 1.5}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
            { path: "/bizdev/briefs", icon: (a) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={a ? '#262626' : '#8E8E8E'} strokeWidth={a ? 2 : 1.5}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg> },
            { path: "/search", icon: (a) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={a ? '#262626' : '#8E8E8E'} strokeWidth={a ? 2 : 1.5}><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg> },
            { path: "/tasks", icon: (a) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={a ? '#262626' : '#8E8E8E'} strokeWidth={a ? 2 : 1.5}><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg> },
            { path: "/production", icon: (a) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={a ? '#262626' : '#8E8E8E'} strokeWidth={a ? 2 : 1.5}><rect x="2" y="2" width="20" height="20" rx="2.18"/><path d="M7 2v20M17 2v20M2 12h20M2 7h5M2 17h5M17 17h5M17 7h5"/></svg> },
          ].map((item, i) => {
            const active = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            return (
              <div key={i} onClick={() => navigate(item.path)}
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '8px 0' }}>
                {item.icon(active)}
              </div>
            );
          })}
        </nav>
      )}

      {/* ═══ CREATE BOTTOM SHEET ═══ */}
      {createOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200 }} onClick={(e) => { if (e.target === e.currentTarget) setCreateOpen(false); }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)' }} onClick={() => setCreateOpen(false)} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#FFF', borderRadius: '16px 16px 0 0', padding: '16px 16px 32px', animation: 'sheetUp .25s ease-out' }}>
            <div style={{ width: 36, height: 4, background: '#C7C7C7', borderRadius: 2, margin: '0 auto 16px' }} />
            {[
              { icon: '☐', label: 'New Task', sub: 'Create for yourself or team', action: () => { setCreateOpen(false); navigate('/tasks/new'); } },
              { icon: '◻', label: 'New Brief', sub: 'Start a client brief', action: () => { setCreateOpen(false); navigate('/bizdev/briefs'); /* TODO: open add modal */ } },
              { icon: '◈', label: 'New Lead', sub: 'Add to pipeline', action: () => { setCreateOpen(false); navigate('/leads'); } },
            ].map((opt, i) => (
              <div key={i} onClick={opt.action}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: i < 2 ? '1px solid #EFEFEF' : 'none', cursor: 'pointer' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#EFEFEF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{opt.icon}</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: '#262626' }}>{opt.label}</div>
                  <div style={{ fontSize: 12, color: '#8E8E8E' }}>{opt.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <GlobalSearch open={searchOpen} onOpen={() => setSearchOpen(true)} onClose={() => setSearchOpen(false)} />

      {/* Keyboard Shortcuts */}
      {showShortcuts && <KeyboardShortcuts onClose={() => setShowShortcuts(false)} />}

      {/* Daily Priority Modals */}
      {showPriorityModal && dailyPriorities && (
        <DailyPriorityModal data={dailyPriorities} mandatory={prioritiesBlocked} onClose={prioritiesBlocked ? undefined : () => setShowPriorityModal(false)} onSave={() => { setShowPriorityModal(false); setPrioritiesBlocked(false); loadDailyPriorities(); }} />
      )}
      {showReflectionModal && dailyPriorities?.today && (
        <ReflectionModal todayData={dailyPriorities.today} onClose={() => setShowReflectionModal(false)} onSave={() => { setShowReflectionModal(false); loadDailyPriorities(); }} />
      )}

      {/* Idle Screensaver */}
      {showEODOverlay && <EODUpdateOverlay onComplete={() => setShowEODOverlay(false)} />}
      {idle && <IdleScreensaver onDismiss={() => { setIdle(false); sounds.resume(); sounds.select(); showToast(`${getGreeting()} ${user?.name?.split(' ')[0] || 'team'}. ${getWellnessTip()}`); }} />}
      <PWAInstallPrompt />
      <MobileProfileSheet open={profileOpen} onClose={() => setProfileOpen(false)} />

      <style>{`
        @media (max-width: 1023px) {
          .dm-layout-grid { grid-template-columns: 1fr !important; max-width: 100vw !important; }
          .dm-main-col { grid-column: 1 / -1 !important; max-width: 100vw !important; }
          .dm-main-content { padding: 0 !important; padding-bottom: 60px !important; max-width: 100vw !important; }
          .dm-ham-btn { display: none !important; }
          .dm-sidebar { display: none !important; }
          .dm-sidebar.\!flex { display: flex !important; }
        }
        @media (max-width: 768px) {
          .dm-search-kbd { display: none; }
          .dm-clock { display: none; }
          .dm-activity-pulse { display: none !important; }
          .dm-streak-badge { display: none !important; }
        }
        @media (max-width: 600px) { .dm-streak-badge { display: none !important; } }
      `}</style>
    </div>
  );
}
